import { Button } from '#app/components/Buttons/Button.js';
import { type ToastType } from '#app/components/Toasts/types.js';
import { type Theme } from '#app/theme/index.js';
import { css, keyframes, styled } from 'styled-components';

// TODO deduplicate values from
// import { NAVBAR_HEIGHT, NAVBAR_HEIGHT_SM } from '#app/components/Navigation/components/base/constants';
const NAVBAR_HEIGHT_SM = 64;
const NAVBAR_HEIGHT = 80;

const enter = keyframes`
  from {
    transform: translateX(40px) scale(.6);
    opacity: .5;
  }

  to {
    transform: translateX(0) scale(1);
    opacity: 1;
  }
`;

const exit = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }

  to {
    transform: translateX(40px);
    opacity: 0;
  }
`;
const getAnimationStyles = (height: number | undefined, visible: boolean) => {
  if (height) {
    if (visible) {
      return css`
        animation: ${enter} 300ms cubic-bezier(0.21, 1.02, 0.73, 1) forwards;
      `;
    }

    return css`
      animation: ${exit} 600ms cubic-bezier(0.06, 0.71, 0.55, 1) forwards;
      pointer-events: none;
    `;
  }

  return css`
    opacity: 0;
  `;
};

const getToastVariantColor = ({
  theme,
  type,
}: {
  theme: Theme;
  type: ToastType;
}): string => {
  switch (type) {
    case 'content': {
      return theme.colorsV2.white;
    }

    case 'error': {
      return theme.colorsV2.red40;
    }

    case 'success': {
      return theme.colorsV2.green50;
    }

    case 'warning': {
      return theme.colors.yellow40;
    }

    case 'info':
    default: {
      return theme.colorsV2.blue40;
    }
  }
};

const getToastVariantBodyTextColor = ({
  theme,
  type,
}: {
  theme: Theme;
  type: ToastType;
}): string => {
  switch (type) {
    case 'content': {
      return theme.colorsV2.white;
    }

    case 'error': {
      return theme.colorsV2.red70;
    }

    case 'success': {
      return theme.colorsV2.green70;
    }

    case 'warning': {
      return theme.colors.yellow70;
    }

    case 'info':
    default: {
      return theme.colorsV2.blue70;
    }
  }
};

const getToastVariantIconBackgroundColor = ({
  theme,
  type,
}: {
  theme: Theme;
  type: ToastType;
}): string => {
  switch (type) {
    case 'content': {
      return theme.colorsV2.white;
    }

    case 'error': {
      return theme.colorsV2.red10;
    }

    case 'success': {
      return theme.colorsV2.green05;
    }

    case 'warning': {
      return theme.colors.yellow10;
    }

    case 'info':
    default: {
      return theme.colorsV2.blue05;
    }
  }
};

const getToastVariantIconColor = ({
  theme,
  type,
}: {
  theme: Theme;
  type: ToastType;
}): string => {
  switch (type) {
    case 'content': {
      return theme.colorsV2.white;
    }

    case 'error': {
      return theme.colorsV2.red70;
    }

    case 'success': {
      return theme.colorsV2.green70;
    }

    case 'warning': {
      return theme.colors.yellow70;
    }

    case 'info':
    default: {
      return theme.colorsV2.blue70;
    }
  }
};

const getToastAdditionalBorderStyles = ({
  theme,
  type,
}: {
  theme: Theme;
  type: ToastType;
}) => {
  if (type === 'content') {
    return css`
      border-color: ${theme.colorsV2.gray50};
      border-radius: 5px;

      ${theme.mediaQueries.md} {
        border-radius: 12px;
      }
    `;
  }

  return css`
    border-color: ${getToastVariantColor({ theme, type })};
    border-radius: 10px;
  `;
};

export const Container = styled.div<{
  offset: number;
}>`
  ${({ offset, theme }) => css`
    position: fixed;
    top: calc(${NAVBAR_HEIGHT_SM + 8}px);
    right: 0;
    left: 0;
    z-index: ${theme.zIndices.toaster};
    display: flex;
    transition: all 200ms cubic-bezier(0.21, 1.02, 0.73, 1);
    transform: translateY(${offset}px);
    margin: 0 8px 8px;

    ${theme.mediaQueries.sm} {
      top: calc(${NAVBAR_HEIGHT + 10}px);
      margin: 0 16px 16px;
      left: unset;
    }
  `}
`;

export const Body = styled.div<{
  $height: number | undefined;
  $type: ToastType;
  $visible: boolean;
}>`
  ${({ $height, $type, $visible, theme }) => css`
    display: flex;
    flex-direction: row;
    align-items: center;
    position: relative;
    padding: 12px;
    gap: 16px;

    width: 100%;
    color: ${getToastVariantBodyTextColor({ theme, type: $type })};
    background-color: ${theme.colorsV2.white};
    box-shadow: ${theme.boxShadow.toast};

    border-width: 1px;
    border-style: solid;
    ${getToastAdditionalBorderStyles({ theme, type: $type })}

    ${getAnimationStyles($height, $visible)}

    ${theme.mediaQueries.sm} {
      max-width: 360px;
    }

    ${theme.mediaQueries.md} {
      min-width: 360px;
      max-width: 440px;
      padding: 16px;
    }
  `}
`;

export const MessageActionsColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 16px;
`;

export const Message = styled.div`
  ${({ theme }) => css`
    flex: 1;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.5;

    ${theme.mediaQueries.md} {
      font-size: 16px;
    }
  `}
`;

export const FloatingDismissButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
`;

export const DismissButton = styled(Button)`
  display: inline-flex;
  width: fit-content;
  margin-left: auto;
`;

export const IconContainer = styled.div<{ $type: ToastType }>`
  ${({ $type, theme }) => css`
    background-color: ${getToastVariantIconBackgroundColor({ theme, type: $type })};
    border-radius: 50%;

    display: none;
    justify-content: center;
    align-items: center;

    padding: 8px;

    

    ${theme.mediaQueries.sm} {
      display: flex;
      & > * {
        color: ${getToastVariantIconColor({ theme, type: $type })}};
      }
    }
  `}
`;
